import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import { Send, RefreshCcw, Trash2, MessageCircle, CheckCircle2, ExternalLink, Bot, Mail, Paperclip, UserPlus, Phone, Link2, X, Edit3, ShieldCheck } from "lucide-react";

export default function Notifications() {
    const [status, setStatus] = useState(null);
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);
    const [testMsg, setTestMsg] = useState("Hello from A W Constructions CFO Command Center 🚀");
    const [testChat, setTestChat] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [s, c] = await Promise.all([
                api.get("/notify/telegram/status"),
                api.get("/notify/telegram/chats"),
            ]);
            setStatus(s.data);
            setChats(c.data.chats || []);
        } catch (e) {
            toast.error("Failed to load Telegram status");
        } finally {
            setLoading(false);
        }
    };

    const poll = async () => {
        setPolling(true);
        try {
            const r = await api.post("/notify/telegram/poll");
            if (r.data.new_chats?.length > 0) {
                toast.success(`Registered ${r.data.new_chats.length} new chat(s)!`);
            } else if (r.data.total_registered === 0) {
                toast.info("No messages received yet — send /start to @awc_cfo_bot first.");
            } else {
                toast.info("No new chats detected.");
            }
            await load();
        } catch (e) {
            toast.error("Poll failed: " + (e.response?.data?.detail || e.message));
        } finally {
            setPolling(false);
        }
    };

    const removeChat = async (chatId) => {
        try {
            await api.delete(`/notify/telegram/chats/${chatId}`);
            toast.success("Removed chat");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const sendTest = async () => {
        if (!testMsg.trim()) { toast.error("Message required"); return; }
        try {
            const body = { message: testMsg, markdown: false };
            if (testChat) body.chat_id = Number(testChat);
            const r = await api.post("/notify/telegram/send", body);
            if (testChat) {
                toast.success(`Sent to chat ${testChat}`);
            } else {
                toast.success(`Broadcast → ${r.data.sent} sent, ${r.data.failed} failed`);
            }
        } catch (e) {
            toast.error("Send failed: " + (e.response?.data?.detail || e.message));
        }
    };

    useEffect(() => { load(); }, []);

    const bot = status?.bot || {};
    const botUsername = bot.username;

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// SHARING & NOTIFICATIONS</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <MessageCircle className="text-[var(--brand-amber)]" size={26} />
                    Notification Channels
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Wire agents to push digests to Telegram · Email board-packs via Gmail/Yahoo SMTP
                </p>
            </div>

            {loading && <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Loading…</div>}

            {!loading && (
                <>
                    {/* Bot status */}
                    <Panel title="Telegram Bot" subtitle={bot.configured ? "Connected ✅" : "Not configured"}>
                        {bot.configured ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Bot name</div>
                                    <div className="font-display font-bold text-[var(--text-primary)] flex items-center gap-2 mt-1">
                                        <Bot size={16} className="text-[var(--brand-amber)]" /> {bot.first_name}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Handle</div>
                                    <a
                                        href={`https://t.me/${botUsername}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="tg-bot-link"
                                        className="font-mono-fin text-[var(--brand-amber)] flex items-center gap-1.5 mt-1 hover:underline"
                                    >
                                        @{botUsername} <ExternalLink size={12} />
                                    </a>
                                </div>
                                <div>
                                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Registered chats</div>
                                    <div className="font-display font-black text-2xl text-[var(--text-primary)] mt-1">{chats.length}</div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-[var(--neg)] text-sm">Set TELEGRAM_BOT_TOKEN in backend/.env and restart.</div>
                        )}
                    </Panel>

                    {/* Onboarding instructions */}
                    <Panel title="How to add a subscriber (30 seconds)" subtitle="One-time per person">
                        <ol className="space-y-2 text-sm font-body list-decimal list-inside text-[var(--text-primary)]/90">
                            <li>
                                Open Telegram on your phone → tap search → type{" "}
                                <a
                                    href={`https://t.me/${botUsername}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[var(--brand-amber)] font-mono-fin"
                                >
                                    @{botUsername || "your_bot"}
                                </a>{" "}
                                → open the bot.
                            </li>
                            <li>Send <span className="font-mono-fin bg-[var(--bg-base)] px-2 py-0.5 border border-panel">/start</span> or any message to the bot.</li>
                            <li>Click <b>Fetch New Chat IDs</b> below — the app auto-registers you.</li>
                            <li>Repeat for every family member / colleague / group chat you want to include.</li>
                        </ol>
                        <button
                            data-testid="tg-poll-btn"
                            onClick={poll}
                            disabled={polling}
                            className="mt-4 bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                            <RefreshCcw size={12} className={polling ? "animate-spin" : ""} />
                            {polling ? "Polling Telegram…" : "Fetch New Chat IDs"}
                        </button>
                    </Panel>

                    {/* Telegram Directory */}
                    <TelegramDirectory chats={chats} onRefresh={load} />

                    {/* Registered chats */}
                    <Panel title={`Registered Chats · ${chats.length}`} subtitle="These will receive every /notify/telegram/digest broadcast">
                        {chats.length === 0 && (
                            <div className="text-xs font-mono-fin text-[var(--text-secondary)] py-3">
                                No chats yet. Send /start to your bot then click Fetch.
                            </div>
                        )}
                        <div className="space-y-2">
                            {chats.map((c) => (
                                <div key={c.chat_id} data-testid={`tg-chat-${c.chat_id}`} className="flex items-center justify-between border border-panel p-3 bg-[var(--bg-base)]">
                                    <div>
                                        <div className="font-display font-bold text-[var(--text-primary)]">
                                            {c.first_name} {c.last_name || ""} {c.username ? `(@${c.username})` : ""}
                                        </div>
                                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-0.5">
                                            chat_id {c.chat_id} · {c.type} · last: {c.last_seen_message || "—"}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeChat(c.chat_id)}
                                        className="text-[var(--neg)] hover:text-[var(--text-primary)] p-2"
                                        title="Remove"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </Panel>

                    {/* Test send */}
                    {chats.length > 0 && (
                        <Panel title="Test Message" subtitle="Send a manual message to verify wiring">
                            <div className="space-y-2">
                                <textarea
                                    data-testid="tg-test-msg"
                                    value={testMsg}
                                    onChange={(e) => setTestMsg(e.target.value)}
                                    rows={3}
                                    className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body focus:border-[var(--brand-amber)] focus:outline-none"
                                />
                                <div className="flex items-center gap-2">
                                    <select
                                        data-testid="tg-test-chat"
                                        value={testChat}
                                        onChange={(e) => setTestChat(e.target.value)}
                                        className="bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin"
                                    >
                                        <option value="">Broadcast to all</option>
                                        {chats.map((c) => (
                                            <option key={c.chat_id} value={c.chat_id}>
                                                {`${c.first_name || "chat"} (${c.chat_id})`}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        data-testid="tg-test-send"
                                        onClick={sendTest}
                                        className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-2"
                                    >
                                        <Send size={12} /> Send
                                    </button>
                                </div>
                            </div>
                        </Panel>
                    )}

                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] pt-4 border-t border-panel flex items-center gap-2">
                        <CheckCircle2 size={12} className="text-[var(--pos)]" />
                        In every agent's detail panel you'll see a "📲 Push to Telegram" button — that broadcasts that agent's digest to all registered chats.
                    </div>

                    <EmailPanel />
                </>
            )}
        </div>
    );
}


// ============ Telegram Directory (owner ↔ chat_id) ============
const ROLE_OPTS = ["MD", "CFO", "ProjectManager", "Accountant", "Board", "Family", "Other"];

function TelegramDirectory({ chats, onRefresh }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [edit, setEdit] = useState(null);
    const [linking, setLinking] = useState(null); // {entry, selectedChat}

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/notify/telegram/directory");
            setEntries(r.data.entries || []);
        } catch (e) {
            toast.error("Failed to load directory");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this authorized contact?")) return;
        try {
            await api.delete(`/notify/telegram/directory/${id}`);
            toast.success("Removed");
            load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const doLink = async () => {
        if (!linking?.selectedChat) return toast.error("Pick a Telegram chat first");
        try {
            await api.post(`/notify/telegram/directory/${linking.entry.id}/link`, { chat_id: Number(linking.selectedChat) });
            toast.success(`${linking.entry.name} linked to Telegram`);
            setLinking(null);
            load();
            onRefresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Link failed");
        }
    };

    const unlink = async (id) => {
        try {
            await api.post(`/notify/telegram/directory/${id}/unlink`);
            toast.success("Unlinked — chat_id detached");
            load();
        } catch (e) {
            toast.error("Unlink failed");
        }
    };

    const unlinkedChats = chats.filter((c) => !entries.some((e) => e.chat_id === c.chat_id));

    return (
        <Panel
            title="Authorized Contacts · Telegram Directory"
            subtitle="Register a person by phone → they DM the bot → link their chat_id → they receive alerts"
            right={
                <button
                    data-testid="btn-add-tg-directory"
                    onClick={() => setEdit({
                        name: "", phone: "", role: "CFO",
                        is_primary: false, can_command: true, receives_broadcasts: true, notes: "",
                    })}
                    className="flex items-center gap-1 px-3 py-1.5 bg-[var(--brand-amber)] text-black text-[10px] font-mono-fin uppercase font-bold"
                >
                    <UserPlus size={12} /> Add Contact
                </button>
            }
        >
            {loading && <div className="text-xs font-mono-fin text-[var(--text-secondary)] py-2">Loading…</div>}
            {!loading && entries.length === 0 && (
                <div className="text-xs font-mono-fin text-[var(--text-secondary)] py-2">
                    No authorized contacts yet. Click <b className="text-[var(--brand-amber)]">Add Contact</b> to register the first person (e.g. Talha · 9755099503).
                </div>
            )}
            <div className="space-y-2">
                {entries.map((e) => (
                    <div key={e.id} data-testid={`tg-dir-row-${e.id}`} className="border border-panel p-3 bg-[var(--bg-base)]">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-display font-bold text-[var(--text-primary)]">{e.name}</span>
                                    {e.is_primary && (
                                        <span className="text-[9px] font-mono-fin uppercase bg-[var(--brand-amber)] text-black px-1.5 py-0.5">Primary</span>
                                    )}
                                    <span className="text-[9px] font-mono-fin uppercase text-[var(--text-secondary)] border border-[var(--border)] px-1.5 py-0.5">{e.role}</span>
                                    {e.linked ? (
                                        <span className="text-[9px] font-mono-fin uppercase bg-[var(--pos)]/20 text-[var(--pos)] px-1.5 py-0.5 flex items-center gap-1">
                                            <CheckCircle2 size={10} /> Linked
                                        </span>
                                    ) : (
                                        <span className="text-[9px] font-mono-fin uppercase bg-[var(--brand-amber)]/20 text-[var(--brand-amber)] px-1.5 py-0.5">Awaiting Link</span>
                                    )}
                                </div>
                                <div className="mt-1 text-[11px] font-mono-fin text-[var(--text-secondary)] flex items-center gap-3 flex-wrap">
                                    <span className="flex items-center gap-1"><Phone size={10} /> {e.phone}</span>
                                    {e.chat_id && <span>chat_id {e.chat_id}</span>}
                                    {e.chat_first_name && <span>({e.chat_first_name}{e.chat_username ? ` · @${e.chat_username}` : ""})</span>}
                                </div>
                                <div className="mt-1.5 text-[10px] font-mono-fin text-[var(--text-muted)] flex items-center gap-3">
                                    <span>Broadcasts: {e.receives_broadcasts ? "✓" : "✗"}</span>
                                    <span>Commands: {e.can_command ? "✓" : "✗"}</span>
                                    {e.notes && <span>· {e.notes}</span>}
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                {!e.linked && (
                                    <button
                                        data-testid={`btn-link-${e.id}`}
                                        onClick={() => setLinking({ entry: e, selectedChat: unlinkedChats[0]?.chat_id || "" })}
                                        className="flex items-center gap-1 px-2 py-1 border border-[var(--brand-amber)] text-[var(--brand-amber)] text-[10px] font-mono-fin uppercase hover:bg-[var(--brand-amber)]/10"
                                        title={unlinkedChats.length === 0 ? "No unlinked chats — ask them to DM the bot first" : ""}
                                    >
                                        <Link2 size={10} /> Link
                                    </button>
                                )}
                                {e.linked && (
                                    <button
                                        data-testid={`btn-unlink-${e.id}`}
                                        onClick={() => unlink(e.id)}
                                        className="flex items-center gap-1 px-2 py-1 border border-[var(--border)] text-[var(--text-secondary)] text-[10px] font-mono-fin uppercase hover:text-[var(--text-primary)]"
                                    >
                                        Unlink
                                    </button>
                                )}
                                <button
                                    data-testid={`btn-edit-dir-${e.id}`}
                                    onClick={() => setEdit(e)}
                                    className="flex items-center gap-1 px-2 py-1 border border-[var(--border)] text-[var(--brand-amber)] text-[10px] font-mono-fin uppercase"
                                >
                                    <Edit3 size={10} /> Edit
                                </button>
                                <button
                                    data-testid={`btn-delete-dir-${e.id}`}
                                    onClick={() => remove(e.id)}
                                    className="flex items-center gap-1 px-2 py-1 border border-[var(--neg)] text-[var(--neg)] text-[10px] font-mono-fin uppercase"
                                >
                                    <Trash2 size={10} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {edit && (
                <DirectoryEditModal
                    entry={edit}
                    onClose={() => setEdit(null)}
                    onDone={() => { setEdit(null); load(); }}
                />
            )}
            {linking && (
                <LinkModal
                    entry={linking.entry}
                    unlinkedChats={unlinkedChats}
                    selectedChat={linking.selectedChat}
                    onPick={(cid) => setLinking({ ...linking, selectedChat: cid })}
                    onConfirm={doLink}
                    onClose={() => setLinking(null)}
                />
            )}
        </Panel>
    );
}

function DirectoryEditModal({ entry, onClose, onDone }) {
    const [f, setF] = useState({ ...entry });
    const isEdit = Boolean(entry.id);
    const save = async () => {
        if (!f.name?.trim()) return toast.error("Name required");
        if (!f.phone?.trim()) return toast.error("Phone required");
        try {
            const body = {
                name: f.name.trim(), phone: f.phone.trim(), role: f.role,
                is_primary: !!f.is_primary, can_command: !!f.can_command,
                receives_broadcasts: !!f.receives_broadcasts, notes: f.notes || "",
            };
            if (isEdit) {
                await api.put(`/notify/telegram/directory/${entry.id}`, body);
                toast.success(`${f.name} updated`);
            } else {
                await api.post(`/notify/telegram/directory`, body);
                toast.success(`${f.name} added`);
            }
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/80 z-50 flex items-center justify-center p-4" data-testid="modal-tg-directory">
            <div className="bg-[var(--panel)] border border-[var(--border)] w-full max-w-lg">
                <div className="flex justify-between items-center p-4 border-b border-[var(--border)]">
                    <h3 className="font-display text-xl font-bold">{isEdit ? `Edit · ${entry.name}` : "Add Authorized Contact"}</h3>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1"><X size={18} /></button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <DField label="Name" cls="col-span-2">
                        <input data-testid="input-dir-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Dr Sayed Talha Sayeed" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </DField>
                    <DField label="Phone">
                        <input data-testid="input-dir-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="9755099503" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm font-mono-fin"/>
                    </DField>
                    <DField label="Role">
                        <select data-testid="input-dir-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm">
                            {ROLE_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </DField>
                    <DField label="Primary Recipient?" cls="col-span-2">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input data-testid="input-dir-primary" type="checkbox" checked={!!f.is_primary} onChange={(e) => setF({ ...f, is_primary: e.target.checked })} className="accent-[var(--brand-amber)]"/>
                            Mark as PRIMARY — first to receive every alert, official owner of this bot
                        </label>
                    </DField>
                    <DField label="Receives Broadcasts?">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input data-testid="input-dir-recv" type="checkbox" checked={!!f.receives_broadcasts} onChange={(e) => setF({ ...f, receives_broadcasts: e.target.checked })} className="accent-[var(--brand-amber)]"/>
                            Yes
                        </label>
                    </DField>
                    <DField label="Can Command Bot?">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input data-testid="input-dir-cmd" type="checkbox" checked={!!f.can_command} onChange={(e) => setF({ ...f, can_command: e.target.checked })} className="accent-[var(--brand-amber)]"/>
                            Yes
                        </label>
                    </DField>
                    <DField label="Notes" cls="col-span-2">
                        <textarea data-testid="input-dir-notes" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} placeholder="e.g. Owner's personal number — bind Telegram after DM to @awc_cfo_bot" className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-1.5 text-sm"/>
                    </DField>
                </div>
                <div className="flex justify-end gap-2 p-4 border-t border-[var(--border)]">
                    <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                    <button data-testid="btn-save-tg-directory" onClick={save} className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold">
                        {isEdit ? "Update" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function LinkModal({ entry, unlinkedChats, selectedChat, onPick, onConfirm, onClose }) {
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/80 z-50 flex items-center justify-center p-4" data-testid="modal-tg-link">
            <div className="bg-[var(--panel)] border border-[var(--border)] w-full max-w-lg">
                <div className="flex justify-between items-center p-4 border-b border-[var(--border)]">
                    <h3 className="font-display text-xl font-bold flex items-center gap-2"><Link2 size={18} className="text-[var(--brand-amber)]"/> Bind {entry.name} to Telegram</h3>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    <div className="text-xs font-mono-fin text-[var(--text-secondary)] leading-relaxed">
                        <b className="text-[var(--brand-amber)]">Step 1:</b> Ask <b className="text-[var(--text-primary)]">{entry.name}</b> ({entry.phone}) to send <span className="font-mono-fin bg-[var(--bg-base)] px-2 py-0.5 border border-[var(--border)]">/start</span> to your Telegram bot.<br/><br/>
                        <b className="text-[var(--brand-amber)]">Step 2:</b> Return here and pick their fresh chat_id below:
                    </div>
                    {unlinkedChats.length === 0 ? (
                        <div className="border border-[var(--neg)]/40 bg-[var(--neg)]/5 p-3 text-xs">
                            <b className="text-[var(--neg)]">No unlinked chats found.</b> Ask them to DM the bot first, then click <b>Fetch New Chat IDs</b> on this page, then reopen this dialog.
                        </div>
                    ) : (
                        <select
                            data-testid="select-link-chat"
                            value={selectedChat}
                            onChange={(e) => onPick(e.target.value)}
                            className="w-full bg-[var(--surface)] border border-[var(--border)] px-2 py-2 text-sm"
                        >
                            {unlinkedChats.map((c) => (
                                <option key={c.chat_id} value={c.chat_id}>
                                    {c.first_name || "chat"} {c.last_name || ""} {c.username ? `(@${c.username})` : ""} · chat_id {c.chat_id}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                <div className="flex justify-end gap-2 p-4 border-t border-[var(--border)]">
                    <button onClick={onClose} className="px-4 py-1.5 border border-[var(--border)] text-xs font-mono-fin uppercase">Cancel</button>
                    <button
                        data-testid="btn-confirm-link"
                        onClick={onConfirm}
                        disabled={unlinkedChats.length === 0}
                        className="px-4 py-1.5 bg-[var(--brand-amber)] text-black text-xs font-mono-fin uppercase font-bold disabled:opacity-50"
                    >
                        Confirm Link
                    </button>
                </div>
            </div>
        </div>
    );
}

function DField({ label, children, cls }) {
    return (
        <div className={cls || ""}>
            <label className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] tracking-wider mb-1 block">{label}</label>
            {children}
        </div>
    );
}


// ============ Email Panel (Gmail / Yahoo SMTP) ============
function EmailPanel() {
    const [status, setStatus] = useState(null);
    const [to, setTo] = useState("");
    const [subject, setSubject] = useState("Board Update from A W Constructions CFO");
    const [body, setBody] = useState("Attaching the latest CFO digest.\n\n— Board Office");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/notify/email/status");
            setStatus(r.data);
        } catch (e) {
            setStatus({ configured: false });
        }
    };
    useEffect(() => { load(); }, []);

    const send = async () => {
        const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
        if (recipients.length === 0) { toast.error("At least one recipient required"); return; }
        if (!subject.trim()) { toast.error("Subject required"); return; }
        setBusy(true);
        try {
            await api.post("/notify/email/send", { to: recipients, subject, body });
            toast.success(`Email sent to ${recipients.length} recipient(s)`);
        } catch (e) {
            toast.error("Email failed: " + (e.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    };

    return (
        <Panel title="Email (Gmail / Yahoo SMTP)" subtitle={status?.configured ? `Connected · ${status.user} · ${status.mode}` : "Not configured — set SMTP_HOST/SMTP_USER/SMTP_PASSWORD in backend/.env"}>
            {!status?.configured ? (
                <div className="text-xs font-body text-[var(--text-primary)]/80 space-y-3">
                    <p>To activate email, add these lines to <span className="font-mono-fin text-[var(--brand-amber)]">backend/.env</span> and restart the backend:</p>
                    <pre className="bg-[var(--bg-base)] border border-panel p-3 text-[11px] font-mono-fin overflow-x-auto">{`SMTP_HOST=smtp.gmail.com      # or smtp.mail.yahoo.com
SMTP_PORT=587                  # 465 for Yahoo (SMTPS)
SMTP_USER=you@gmail.com
SMTP_PASSWORD=xxxxxxxxxxxxxxxx  # 16-char App Password
SMTP_FROM=you@gmail.com
SMTP_FROM_NAME=A W Constructions CFO
SMTP_USE_TLS=true              # false for Yahoo port 465`}</pre>
                    <div className="text-[11px] text-[var(--text-secondary)]">
                        Gmail App Password → <a target="_blank" rel="noreferrer" href="https://myaccount.google.com/apppasswords" className="text-[var(--brand-amber)] hover:underline">myaccount.google.com/apppasswords</a><br />
                        Yahoo App Password → <a target="_blank" rel="noreferrer" href="https://login.yahoo.com/account/security" className="text-[var(--brand-amber)] hover:underline">login.yahoo.com/account/security</a>
                    </div>
                    <button
                        data-testid="email-refresh"
                        onClick={load}
                        className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1"
                    >
                        <RefreshCcw size={12} /> Recheck Status
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Sender</div>
                            <div className="font-mono-fin text-sm text-[var(--text-primary)] truncate flex items-center gap-1"><Mail size={12} className="text-[var(--brand-amber)]" /> {status.from || status.user}</div>
                        </div>
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">SMTP Host</div>
                            <div className="font-mono-fin text-sm text-[var(--text-primary)] truncate">{status.host}:{status.port}</div>
                        </div>
                        <div className="border border-panel bg-[var(--bg-base)] p-3">
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Mode</div>
                            <div className="font-mono-fin text-sm text-[var(--pos)]">{status.mode}</div>
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">To (comma separated)</div>
                        <input
                            data-testid="email-to"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder="md@awconstructions.in, board@awconstructions.in"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Subject</div>
                        <input
                            data-testid="email-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Body</div>
                        <textarea
                            data-testid="email-body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={5}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-body focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                    </div>
                    <button
                        data-testid="email-send"
                        onClick={send}
                        disabled={busy}
                        className="bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        <Send size={12} /> {busy ? "Sending…" : "Send Email"}
                    </button>
                    <div className="text-[10px] font-mono-fin text-[var(--text-muted)] flex items-center gap-1 pt-2 border-t border-panel">
                        <Paperclip size={10} /> Use each agent&apos;s Email PDF button in the AI Hub to email the board-pack PDF automatically.
                    </div>
                </div>
            )}
        </Panel>
    );
}
